// src/context/AuthContext.js
// Global authentication state using React Context.
// Wrap your app with <AuthProvider> and use the useAuth() hook anywhere.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../services/api';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true); // true while we check existing session

  // On mount, check if there's a valid stored session
  useEffect(() => {
    const storedUser  = localStorage.getItem('whl_user');
    const storedToken = localStorage.getItem('whl_token');

    if (storedUser && storedToken) {
      // Verify the token is still valid server-side
      authApi.me()
        .then(res => {
          // Merge fresh functions from server with stored user data
          const stored = JSON.parse(storedUser);
          const fresh  = res.data.user || {};
          const merged = {
            ...stored,
            functions: fresh.functions || stored.functions,
            ...(fresh.timezone ? { timezone: fresh.timezone } : {}),
          };
          localStorage.setItem('whl_user', JSON.stringify(merged));
          setUser(merged);
        })
        .catch(() => {
          localStorage.removeItem('whl_token');
          localStorage.removeItem('whl_user');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    const response = await authApi.login(credentials);
    const { token, user: userData } = response.data;

    sessionStorage.removeItem('whl_lastFilter');
    localStorage.setItem('whl_token', token);
    localStorage.setItem('whl_user', JSON.stringify(userData));
    setUser(userData);

    return userData;
  };

  const loginWithData = (token, userData) => {
    localStorage.setItem('whl_token', token);
    localStorage.setItem('whl_user', JSON.stringify(userData));
    setUser(userData);
  };

  const updateTimezone = async (timezone) => {
    await api.put('/users/me/timezone', { timezone });
    setUser(prev => {
      const updated = { ...prev, timezone };
      localStorage.setItem('whl_user', JSON.stringify(updated));
      return updated;
    });
  };

  const updateIdioma = async (idioma) => {
    await api.put('/users/me/idioma', { idioma });
    setUser(prev => {
      const updated = { ...prev, idioma };
      localStorage.setItem('whl_user', JSON.stringify(updated));
      return updated;
    });
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Even if the server call fails, clear local state
    } finally {
      localStorage.removeItem('whl_token');
      localStorage.removeItem('whl_user');
      sessionStorage.removeItem('whl_lastFilter');
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithData, logout, updateTimezone, updateIdioma }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
